<!-- @prettier -->
<template>
  <PortalWidget :widget="widget">
    <div v-if="!isLoading">
      <ValidationObserver
        v-slot="{ handleSubmit, invalid }"
        ref="form"
      >
        <form
          class="form-horizontal"
          novalidate
          @submit.prevent="handleSubmit(createUpdateAccount)"
        >
          <fieldset>
            <div class="container-fluid">
              <!-- Country -->
              <div class="form-group">
                <label class="col-sm-3 col-md-3 control-label">
                  {{ $t('ACCOUNT_PAGE_COUNTRY') }}
                  <a
                    v-tooltip="$t('ACCOUNT_PAGE_COUNTRY_TT')"
                    class="tooltip-icon"
                  >
                    <i class="fa m-l-3 fa-question-circle" />
                  </a>
                </label>
                <div class="col-sm-4 col-md-3">
                  <CountrySelector
                    v-model="countryValue"
                    :placeholder="$t('ACCOUNT_PAGE_COUNTRY_DEFAULT')"
                    @change="onCountryChange"
                  />
                </div>
              </div>

              <!-- Time Zone -->
              <div class="form-group">
                <label class="col-sm-3 col-md-3 control-label">
                  {{ $t('ACCOUNT_PAGE_TIME_ZONE') }}
                  <a
                    v-tooltip="$t('ACCOUNT_PAGE_TIME_ZONE_TT')"
                    class="tooltip-icon"
                  >
                    <i class="fa m-l-3 fa-question-circle" />
                  </a>
                </label>

                <ValidationProvider
                  v-slot="{ classes, errors }"
                  rules="required"
                  name="Time Zone"
                >
                  <div
                    class="col-sm-4 col-md-3"
                    :class="classes"
                  >
                    <SearchableDropdown
                      v-model="localAccountInfo.timeZoneId"
                      :config="timeZoneOptions"
                      data-testid="timezone-dropdown"
                      :placeholder="$t('ACCOUNT_PAGE_TIME_ZONE_TT')"
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Company -->
              <div class="form-group">
                <label class="col-sm-3 col-md-3 control-label">
                  {{ $t('ACCOUNT_PAGE_COMPANY') }}
                  <a
                    v-tooltip="$t('ACCOUNT_PAGE_COMPANY_TT')"
                    class="tooltip-icon"
                  >
                    <i class="fa m-l-3 fa-question-circle" />
                  </a>
                </label>

                <ValidationProvider
                  v-slot="{ errors, classes }"
                  rules="required"
                  name="Company"
                >
                  <div
                    class="col-sm-4 col-md-3"
                    :class="classes"
                  >
                    <input
                      v-model="localAccountInfo.name"
                      type="text"
                      class="form-control"
                    >
                    <span class="text-danger">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Address -->
              <div class="form-group">
                <label class="col-sm-3 col-md-3 control-label">
                  {{ $t('ACCOUNT_PAGE_ADDRESS') }}
                  <a
                    v-tooltip="$t('ACCOUNT_PAGE_ADDRESS_TT')"
                    class="tooltip-icon"
                  >
                    <i class="fa m-l-3 fa-question-circle" />
                  </a>
                </label>
                <ValidationProvider
                  v-slot="{ errors, classes }"
                  rules="required"
                  name="Address"
                >
                  <div
                    class="col-sm-4 col-md-3"
                    :class="classes"
                  >
                    <input
                      v-model="localAccountInfo.accountAddress.streetAddress"
                      type="text"
                      class="form-control"
                    >
                    <span class="text-danger">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Address Two -->
              <div class="form-group">
                <label class="col-sm-3 col-md-3 control-label" />
                <div
                  class="col-sm-4 col-md-3"
                >
                  <input
                    v-model="localAccountInfo.accountAddress.addressLine2"
                    type="text"
                    class="form-control"
                    :placeholder="$t('ACCOUNT_PAGE_ADDRESS_TWO_DEFAULT')"
                  >
                </div>
              </div>

              <!-- City -->
              <div class="form-group">
                <label class="col-sm-3 col-md-3 control-label">
                  {{ $t('ACCOUNT_PAGE_CITY') }}
                  <a
                    v-tooltip="$t('ACCOUNT_PAGE_CITY_TT')"
                    class="tooltip-icon"
                  >
                    <i class="fa m-l-3 fa-question-circle" />
                  </a>
                </label>
                <ValidationProvider
                  v-slot="{ errors, classes }"
                  rules="required"
                  name="City"
                >
                  <div
                    class="col-sm-4 col-md-3"
                    :class="classes"
                  >
                    <input
                      v-model="localAccountInfo.accountAddress.city"
                      type="text"
                      class="form-control"
                    >
                    <span class="text-danger">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- State -->
              <div class="form-group">
                <label class="col-sm-3 col-md-3 control-label">
                  {{ $t('ACCOUNT_PAGE_STATE') }}
                  <a
                    v-tooltip="$t('ACCOUNT_PAGE_STATE_TT')"
                    class="tooltip-icon"
                  >
                    <i class="fa m-l-3 fa-question-circle" />
                  </a>
                </label>
                <ValidationProvider
                  v-slot="{ errors, classes }"
                  rules="required"
                  name="State"
                >
                  <div
                    class="col-sm-4 col-md-3"
                    :class="classes"
                  >
                    <SearchableDropdown
                      v-if="countryValue.value === 'US'"
                      v-model="localAccountInfo.accountAddress.region"
                      :config="stateOptions"
                      data-testId="account-state-dropdown"
                      :placeholder="$t('ACCOUNT_PAGE_STATE_DEFAULT')"
                    />
                    <input
                      v-else
                      v-model="localAccountInfo.accountAddress.region"
                      type="text"
                      class="form-control"
                    >
                    <span class="text-danger">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Zip -->
              <div class="form-group">
                <label class="col-sm-4 col-md-3 control-label">
                  {{ $t('ACCOUNT_PAGE_ZIP') }}
                  <a
                    v-tooltip="$t('ACCOUNT_PAGE_ZIP_TT')"
                    class="tooltip-icon"
                  >
                    <i class="fa m-l-3 fa-question-circle" />
                  </a>
                </label>
                <ValidationProvider
                  v-slot="{ errors, classes }"
                  rules="required"
                  name="Zip"
                >
                  <div
                    class="col-sm-3 col-md-3"
                    :class="classes"
                  >
                    <input
                      v-model="localAccountInfo.accountAddress.postalCode"
                      type="text"
                      class="form-control"
                      maxlength="8"
                    >
                    <span class="text-danger">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Submit Button -->
              <div class="form-group">
                <div class="col-sm-9 col-md-9 col-md-offset-3 col-sm-offset-3">
                  <p
                    v-if="triedToSend && invalid"
                    class="m-t-8 m-b-8 bright-red"
                  >
                    <i class="f-s-20 p-r-0 typcn typcn-warning" />{{
                      $t('FORM_VALIDATION_ERROR')
                    }}
                  </p>
                  <PrimaryButton
                    v-tooltip="{ content: $t('ACCOUNT_PAGE_COUNTRY_TT'), hideDelay: 0, showDelay: 0 }"
                    type="submit"
                    size-class="btn-lg"
                    :show-loader-on-form="isUpdating"
                    @click="triedToSend = true"
                  >
                    {{ $t('ACCOUNT_PAGE_UPDATE_BUTTON') }}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </fieldset>
        </form>
      </ValidationObserver>
    </div>

    <div
      v-if="isLoading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'ACCOUNT_DETAILS_LOADING'" />
    </div>
  </PortalWidget>
</template>
<script>
import sharedMessages from '@/core/locales';
import messages from '../locales';
import Loader from '@/core/components/Loader';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import PortalWidget from '@/core/components/PortalWidget.vue';
import CountrySelector from '@/core/components/CountrySelector/CountrySelector.vue';
import PrimaryButton from '@/core/components/PrimaryButton';
import { mapActions, mapState } from 'vuex';
import { accountsService, utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import USStatesList from '@/data/USStatesList';
import _ from 'lodash';

export default {
  name: 'AccountDetails',
  components: {
    Loader,
    SearchableDropdown,
    PortalWidget,
    CountrySelector,
    PrimaryButton,
  },
  data: () => ({
    isLoading: true,
    isUpdating: false,
    triedToSend: false,
    localAccountInfo: {
      id: null,
      accountAddress: {
        countryCode: '',
        streetAddress: '',
        addressLine2: '',
        city: '',
        region: '',
        postalCode: '',
        countryName: ''
      },
      timeZoneId: '',
      name: ''
    },
  }),
  computed: {
    ...mapState({
      accountId: 'currentAccountId',
      currentAccount: 'currentAccount',
    }),

    ...mapState([
      'timezones'
    ]),
    timeZoneOptions() {
      return {
        options: this.timezones,
        ...{
          reduce: (option) => option.id,
          label: 'display',
        },
        placeholder: 'Select Timezone'
      };
    },
    stateOptions() {
      return {
        options: USStatesList,
        reduce: (option) => option.abbreviation,
        label: 'displayName',
        placeholder: this.$t('ACCOUNT_PAGE_STATE_DEFAULT')
      };
    },
    countryValue: {
      get() {
        const label = this.localAccountInfo.accountAddress.countryName;
        const value = this.localAccountInfo.accountAddress.countryCode;

        if (!label || !value) {
          return null;
        }

        return {
          label,
          value,
        };
      },
      set({ label, value }) {
        this.localAccountInfo.accountAddress.countryName = label;
        this.localAccountInfo.accountAddress.countryCode = value;
      },
    },
  },
  beforeMount() {
    this.widget = {
      title:this.$i18n.t('ACCOUNT_PAGE_TITLE'),
      iconClass: 'typcn typcn-business-card'
    };
  },
  created() {
    if (!this.timezones?.length) {
      this.getTimezones();
    }

    this.initializeLocalAccountInfo();
  },
  methods: {
    ...mapActions(['getStates', 'getTimezones']),

    //CRUD
    async createUpdateAccount() {

      this.isUpdating = true;
      this.triedToSend = true;

      const accountData = {
        name: this.localAccountInfo.name,
        timeZoneId: this.localAccountInfo.timeZoneId,
        accountAddress: {
          streetAddress: this.localAccountInfo.accountAddress.streetAddress,
          addressLine2: this.localAccountInfo.accountAddress.addressLine2,
          city: this.localAccountInfo.accountAddress.city,
          region: this.localAccountInfo.accountAddress.region,
          postalCode: this.localAccountInfo.accountAddress.postalCode,
          countryName: this.localAccountInfo.accountAddress.countryName,
          countryCode: this.localAccountInfo.accountAddress.countryCode
        }
      };

      try {
        if (this.localAccountInfo.id) {
          await accountsService.updateAccount(accountData);
          utilities.showToaster('success', this.$t('ACCOUNT_PAGE_UPDATE_MESSAGE'));
          this.triedToSend = false;
        } else {
          await accountsService.createAccount(accountData);
          utilities.showToaster('success', this.$t('ACCOUNT_PAGE_CREATE_MESSAGE'));
          this.triedToSend = false;
          this.$router.push({ name: 'dashboard' });
        }
      } catch (error) {
        if (error.response && error.response.data) {
          const response = error.response.data;
          const errors = [];
          for (const key in response.modelState) {
            errors.push(...response.modelState[key]);
          }
          utilities.showToaster('error', this.$t('ACCOUNT_PAGE_ERROR_MESSAGE') + errors.join(' '));
        } else {
          utilities.showToaster('error', this.$t('GENERIC_ERROR_MESSAGE'));
        }
      }

      this.isUpdating = false;
    },
    initializeLocalAccountInfo() {
      this.localAccountInfo = _.cloneDeep(this.currentAccount);
      this.isLoading = false;
    },
    onCountryChange() {
      this.localAccountInfo.accountAddress.region = '';
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>
